import { useState } from 'react'
import { formatMoney } from '../lib/money.js'

/**
 * Selector de propina del comensal — mismo comportamiento que el modal comensal/
 * barra del POS, estilizado nativo del guest:
 *   - % sugerido (presets del negocio; default 0/10/15/20) mostrando SIEMPRE %/$.
 *   - Personalizado: el comensal elige si captura MONTO ($) o PORCENTAJE (%).
 *   - Vacío = 0 (opcional).
 *   - Tope: máx 100% del consumo (base).
 * Controlado: el padre es dueño de `valueCents` y reacciona a `onChange`.
 */
export function GuestTipSelector({ baseCents, valueCents, onChange, presets = [0, 10, 15, 20] }: {
  baseCents: number
  valueCents: number
  onChange: (tipCents: number) => void
  presets?: number[]
}) {
  const activePct = baseCents > 0 ? Math.round((valueCents / baseCents) * 100) : 0
  const [mode, setMode] = useState<'percent' | 'amount'>(
    presets.includes(activePct) || valueCents === 0 ? 'percent' : 'amount',
  )
  // Personalizado: unidad elegida por el comensal ($ o %). Un solo input; el
  // valor se reinterpreta al cambiar la unidad (el string capturado se conserva).
  const [customUnit, setCustomUnit] = useState<'amount' | 'percent'>('amount')
  const [amountStr, setAmountStr] = useState(valueCents > 0 && !presets.includes(activePct) ? (valueCents / 100).toFixed(2) : '')

  function pickPct(p: number): void {
    onChange(Math.min(Math.round((baseCents * p) / 100), baseCents))
  }
  function applyCustom(s: string, unit: 'amount' | 'percent'): void {
    const n = Number.parseFloat(s.replace(',', '.'))
    if (!Number.isFinite(n) || n <= 0) { onChange(0); return }
    // Tope: máx 100% del consumo (anti fat-finger), en ambas unidades.
    const cents = unit === 'percent'
      ? Math.round((baseCents * Math.min(n, 100)) / 100)
      : Math.min(Math.round(n * 100), baseCents)
    onChange(cents)
  }
  function setAmount(s: string): void {
    setAmountStr(s)
    applyCustom(s, customUnit)
  }
  function switchUnit(unit: 'amount' | 'percent'): void {
    setCustomUnit(unit)
    applyCustom(amountStr, unit)
  }

  // El chip NO seleccionado tiene fondo blanco FIJO (#fff), así que su texto debe
  // ser oscuro FIJO — no `var(--fg)`, que en tema oscuro es claro y quedaba texto
  // casi blanco sobre blanco (invisible). #1D1D1F = mismo ink que los campos de
  // tarjeta del guest.
  const chip = (on: boolean): React.CSSProperties => ({
    flex: '1 1 0', minWidth: 64, padding: '10px 8px', borderRadius: 12,
    border: `1px solid ${on ? 'var(--accent)' : 'var(--line-strong)'}`,
    background: on ? 'var(--accent)' : '#fff', color: on ? 'var(--accent-ink)' : '#1D1D1F',
    cursor: 'pointer', font: 'inherit', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 2, lineHeight: 1.1,
  })

  return (
    <div>
      {/* Toggle %/monto */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {(['percent', 'amount'] as const).map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            style={{
              padding: '7px 16px', borderRadius: 10, cursor: 'pointer', font: 'inherit',
              border: `1px solid ${mode === m ? 'var(--accent)' : 'var(--line-strong)'}`,
              background: mode === m ? 'var(--accent-soft)' : '#fff',
              color: mode === m ? 'var(--accent-2)' : 'var(--fg-muted)', fontWeight: 600,
            }}>
            {m === 'percent' ? 'Sugeridas' : 'Personalizada'}
          </button>
        ))}
      </div>

      {mode === 'percent' ? (
        <div style={{ display: 'flex', gap: 8 }}>
          {presets.map((p) => {
            const on = activePct === p
            return (
              <button key={p} type="button" onClick={() => pickPct(p)} style={chip(on)}>
                {p === 0 ? (
                  <span style={{ fontWeight: 600 }}>Sin</span>
                ) : (
                  <>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{p}%</span>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>{formatMoney(Math.round((baseCents * p) / 100))}</span>
                  </>
                )}
              </button>
            )
          })}
        </div>
      ) : (
        <label style={{ display: 'block' }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: 'var(--fg-muted)', marginBottom: 6 }}>
            Propina personalizada
            {/* Unidad: monto ($) o porcentaje (%) — elección del comensal. */}
            <span style={{ display: 'inline-flex', gap: 4 }}>
              {(['amount', 'percent'] as const).map((u) => (
                <button key={u} type="button" onClick={() => switchUnit(u)}
                  aria-pressed={customUnit === u}
                  style={{
                    padding: '3px 10px', borderRadius: 8, cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700,
                    border: `1px solid ${customUnit === u ? 'var(--accent)' : 'var(--line-strong)'}`,
                    background: customUnit === u ? 'var(--accent)' : '#fff',
                    color: customUnit === u ? 'var(--accent-ink)' : '#1D1D1F',
                  }}>
                  {u === 'amount' ? '$' : '%'}
                </button>
              ))}
            </span>
          </span>
          <div style={{ background: '#fff', border: '1px solid var(--line-strong)', borderRadius: 14, padding: '13px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'var(--fg-muted)' }}>{customUnit === 'amount' ? '$' : '%'}</span>
            <input type="text" inputMode="decimal" value={amountStr} placeholder={customUnit === 'amount' ? '0.00' : '0'}
              onChange={(e) => setAmount(e.currentTarget.value)}
              style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', font: 'inherit', color: '#1D1D1F' }} />
          </div>
          {valueCents > 0 && (
            <span style={{ display: 'block', marginTop: 6, fontSize: 13, color: 'var(--fg-muted)' }}>
              Propina: <strong>{formatMoney(valueCents)}</strong>
            </span>
          )}
        </label>
      )}
    </div>
  )
}
